import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  CircularProgress,
  Alert,
  Button,
  TextField,
} from "@mui/material";
import {
  ShoppingCart as OrderIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import LocalShippingIcon from "@mui/icons-material/LocalShipping"; // SHIPPED
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // DELIVERED
import CancelIcon from "@mui/icons-material/Cancel"; // CANCELLED
import ReportProblemIcon from "@mui/icons-material/ReportProblem"; // HOLD
import InventoryIcon from "@mui/icons-material/Inventory"; // READY_TO_SHIP
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn"; // RTO_LOCKED / RTO_COMPLETE
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"; // DOOR_STEP_EXCHANGED
import PendingActionsIcon from "@mui/icons-material/PendingActions"; // CLAIM_PENDING / PENDING
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"; // CLAIM_RESOLVED
import HighlightOffIcon from "@mui/icons-material/HighlightOff"; // CLAIM_REJECTED
import { useDispatch } from "react-redux";
import { getMeeshoStatisticsList } from "app/redux/slice/meeshoOrderManagementSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import { orderManagementStatisticsList } from "app/routes/constant";
import moment from "moment";
const statisticsIds = [
  "DELIVERED",
  "CURIOR_RETURN",
  "CUSTOMER_RETURN",
  "CANCELLED",
];
const OrderStatistics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [statistics, setStatistics] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalOrder, setTotalOrder] = useState(null);

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
  };
  console.log(statistics);

  useEffect(() => {
    fetchStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilter = () => {
    setLoading(true);
    const params = {};
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      setLoading(false);
      return;
    }

    params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    dispatch(getMeeshoStatisticsList(params))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setTotalOrder(res?.data?.data?.totalOrders);
          const filteredData = res?.data?.data?.statusBreakdown?.filter(
            (item) => statisticsIds?.includes(item?._id)
          );
          setStatistics(filteredData);

          const statusBreakdown = res?.data?.data?.statusBreakdown?.filter(
            (i) => !statisticsIds?.includes(i?._id)
          );
          setStatusBreakdown(statusBreakdown);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const fetchStatistics = async () => {
    setLoading(true);
    setDateRange([null, null]);
    dispatch(getMeeshoStatisticsList({}))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setTotalOrder(res?.data?.data?.totalOrders);

          const filteredData = res?.data?.data?.statusBreakdown?.filter(
            (item) => statisticsIds?.includes(item?._id)
          );
          setStatistics(filteredData);

          const statusBreakdown = res?.data?.data?.statusBreakdown?.filter(
            (i) => !statisticsIds?.includes(i?._id)
          );
          setStatusBreakdown(statusBreakdown);
        } else {
          enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch statistics", { variant: "error" });
      });
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      DELIVERED: <CheckCircleIcon color="success" />,
      CANCELLED: <CancelIcon color="error" />,
      SHIPPED: <LocalShippingIcon color="info" />,
      RTO_LOCKED: <AssignmentReturnIcon color="warning" />,
      RTO_COMPLETE: <AssignmentReturnIcon color="secondary" />,
      RTO_OFD: <PieChartOutlineIcon color="warning" />,
      READY_TO_SHIP: <InventoryIcon color="primary" />,
      HOLD: <ReportProblemIcon color="warning" />,
      DOOR_STEP_EXCHANGED: <SwapHorizIcon color="info" />,
      CLAIM_PENDING: <PendingActionsIcon color="warning" />,
      CLAIM_RESOLVED: <CheckCircleOutlineIcon color="success" />,
      CLAIM_REJECTED: <HighlightOffIcon color="error" />,
      PENDING: <PendingActionsIcon color="default" />,
      LOST: <OrderIcon color="error" />,
      CURIOR_RETURN: <AssignmentReturnIcon color="info" />,
      CUSTOMER_RETURN: <AssignmentReturnIcon color="warning" />,
    };
    return iconMap[status] || <OrderIcon />;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      DELIVERED: theme.palette.success.main,
      CANCELLED: theme.palette.error.main,
      SHIPPED: theme.palette.info.main,
      RTO_LOCKED: theme.palette.warning.main,
      RTO_OFD: theme.palette.warning.main,
      RTO_COMPLETE: theme.palette.secondary.main,
      READY_TO_SHIP: theme.palette.primary.main,
      HOLD: theme.palette.grey[500],
      DOOR_STEP_EXCHANGED: theme.palette.info.light,
      CLAIM_PENDING: theme.palette.warning.light,
      CLAIM_RESOLVED: theme.palette.success.light,
      CLAIM_REJECTED: theme.palette.error.light,
      PENDING: theme.palette.grey[400],
      LOST: theme.palette.error.main,
      CURIOR_RETURN: theme.palette.info.main,
      CUSTOMER_RETURN: theme.palette.warning.light,
    };
    return colorMap[status] || theme.palette.grey[500];
  };

  const formatStatusLabel = (status) => {
    const labelMap = {
      DELIVERED: "Delivered",
      CANCELLED: "Cancelled",
      SHIPPED: "Shipped",
      RTO_LOCKED: "RTO Locked",
      RTO_OFD: "RTO Out for Delivery",
      RTO_COMPLETE: "RTO Complete",
      READY_TO_SHIP: "Ready to Ship",
      HOLD: "Hold",
      DOOR_STEP_EXCHANGED: "Door Step Exchanged",
      CLAIM_PENDING: "Claim Pending",
      CLAIM_RESOLVED: "Claim Resolved",
      CLAIM_REJECTED: "Claim Rejected",
      CURIOR_RETURN: "Courier Return",
      CUSTOMER_RETURN: "Customer Return",
      PENDING: "Pending",
      LOST: "Lost",
    };
    return labelMap[status] || status;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom mb={2}>
        Order Statistics
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={handleDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : ""}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={fetchStatistics}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="h4" component="h1" gutterBottom>
                Total Orders : {totalOrder ?? 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {loading ? (
          <Loader />
        ) : (
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {statistics?.length !== 0 ? (
                statistics?.map((status) => (
                  <Grid item xs={12} sm={6} md={3} key={status?._id}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => {
                        let params = {};
                        if (dateRange[0] === null || dateRange[1] === null) {
                          params = null;
                        } else {
                          params.fromDate = moment(dateRange[0]).format(
                            "YYYY-MM-DD"
                          );
                          params.toDate = moment(dateRange[1]).format(
                            "YYYY-MM-DD"
                          );
                        }

                        navigate(orderManagementStatisticsList(status?._id), {
                          state: params,
                        });
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ mr: 1, color: getStatusColor(status._id) }}>
                          {getStatusIcon(status._id)}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {formatStatusLabel(status._id)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        color={getStatusColor(status._id)}
                      >
                        {status.count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {status.percentage} % of total
                      </Typography>
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Box sx={{ p: 3, width: "100%", textAlign: "center" }}>
                  <Alert severity="info">No Data Found</Alert>
                </Box>
              )}
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} mt={4}>
                <Typography variant="h4" mb={0}>
                  Status Breakdown
                </Typography>
              </Grid>
              {statusBreakdown?.length !== 0 ? (
                statusBreakdown?.map((status) => (
                  <Grid item xs={12} sm={6} md={3} key={status?._id}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => {
                        let params = {};
                        if (dateRange[0] === null || dateRange[1] === null) {
                          params = null;
                        } else {
                          params.fromDate = moment(dateRange[0]).format(
                            "YYYY-MM-DD"
                          );
                          params.toDate = moment(dateRange[1]).format(
                            "YYYY-MM-DD"
                          );
                        }

                        navigate(orderManagementStatisticsList(status?._id), {
                          state: params,
                        });
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ mr: 1, color: getStatusColor(status._id) }}>
                          {getStatusIcon(status._id)}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {formatStatusLabel(status._id)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        color={getStatusColor(status._id)}
                      >
                        {status.count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {status.percentage} % of total
                      </Typography>
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Box sx={{ p: 3, width: "100%", textAlign: "center" }}>
                  <Alert severity="info">No Data Found</Alert>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default OrderStatistics;
